// @flow

import React, {Component} from 'react';
import _ from 'lodash';
import {Icon} from 'react-fa';
import scrollIntoViewIfNeeded from 'scroll-into-view-if-needed';

import type { Element } from 'react';

import Map from './Map.jsx';
import App from './App.jsx';

import {inv_ord, gen_snum, abrev, takeDir, takeFilename, unFuck} from './utils.js';

import type {T_ColOrd} from './types.js';
import type {T_SelPol} from './Toolbar.jsx';

import './css/Stats.css';


export type T_StatRow = {
  Nombre  : string,
  ID      : number,
  Min     : number,
  Max     : number,
  Promedio: number,
  CV      : number,
  Has     : number
};


export type T_StatTotal = {
  ID      : string,
  Min     : number,
  Max     : number,
  Promedio: number,
  CV      : number
};



export type T_Stats = {
  prop : number,
  culti: string,
  elem : string,
  tif  : string,
  png  : string,
  path : string,
  rows : Array<T_StatRow>,
  total: T_StatTotal,
  units: string,
  warn?: string
};



type T_Elem_Path = {
  elem    : string,
  png_path: string
};



type T_StatsProps = {
  stats       : T_Stats,
  sel_pol     : ?T_SelPol,
  num_decimals: number,
  show_elems  : Array<T_Elem_Path>,
  app         : App
};



type T_StatsState = {
  st_col_ord : ?T_ColOrd,
  current_row: number,
  good_elems : Array<T_Elem_Path>,
};



function remove_suffix(eps: Array<T_Elem_Path>): Array<T_Elem_Path> {
  const
    elems  = _.map(eps, 'elem'),
    partss = _.map(elems, elem => elem.split('-')),
    lasts  = _.map(partss, parts => _.last(parts));
  
  if (_.uniq(lasts).length <= 2)
    return eps.map(({elem, png_path}) => ({elem: _.initial(elem.split('-')).join('-'), png_path}));
  
  return eps;
}



export default class Stats extends Component<T_StatsProps, T_StatsState> {
  static keys_rows = ['Home', 'End', 'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown'];
  
  tr_hilight: ?any; // WAS: Element<string>;
  
  click_col_sort: (ev: SyntheticInputEvent<*>)     => void;
  change_to_elem: (ev: SyntheticEvent<*>)          => void;
  change_row    : (ev: SyntheticKeyboardEvent<*>)  => void;
  change_image  : (ev: SyntheticKeyboardEvent<*>)  => void;
  key_down      : (ev: SyntheticKeyboardEvent<*>)  => void;
  
  
  
  constructor(props: T_StatsProps) {
    super(props);
    
    this.state = {
      st_col_ord : null,
      current_row: -1,
      good_elems : remove_suffix(this.props.show_elems)
    };
    
    this.click_col_sort = this.click_col_sort.bind(this);
    this.change_row     = this.change_row.bind(this);
    this.change_image   = this.change_image.bind(this);
    this.key_down       = this.key_down.bind(this);
    this.change_to_elem = this.change_to_elem.bind(this);
  }
  
  
  
  componentDidUpdate() {
    if (this.props.sel_pol && this.tr_hilight) {
      if (this.tr_hilight.scrollIntoViewIfNeeded) {
        this.tr_hilight.scrollIntoViewIfNeeded();
      }
      else {
        scrollIntoViewIfNeeded(this.tr_hilight);
      }
    }
  }
  
  
  
  click_col_sort(ev: SyntheticInputEvent<*>): void {
    const {col} = ev.target.dataset;
    
    this.setState({
      st_col_ord: {
        col,
        ord: this.state.st_col_ord && this.state.st_col_ord.col === col ? inv_ord(this.state.st_col_ord.ord) : 'sort-asc'
      }
    });
  }
  
  
  
  toggle_sort(col: string): Element<string> {
    const data_col = {'data-col': col};
    
    return (
      <a {...data_col} onClick={this.click_col_sort}>
        {(this.state.st_col_ord && this.state.st_col_ord.col === col) &&
          <Icon name={this.state.st_col_ord.ord} />
        }
        {col}
      </a>
    );
  }
  
  
  
  custom_sort(rows: Array<Object>): Array<Object> {
    if (! this.state.st_col_ord) {
      return rows;
    }
    
    const rs = _.sortBy(rows, this.state.st_col_ord.col);
    
    return this.state.st_col_ord != null && this.state.st_col_ord.ord === 'sort-desc' ? _.reverse(rs) : rs;
  }
  
  
  
  click_row = (ev: SyntheticEvent<*> | number): void => {
    this.setState({current_row: typeof ev === 'number' ? ev : parseInt(ev.currentTarget.dataset.num, 10)}, () => {
      const pol = this.props.app.map_ui.map.get_pol(this.props.stats.culti, this.state.current_row);
      
      if (pol && Map.google != null) {
        Map.google.event.trigger(pol, 'click', {});
      }
    });
  }
  
  
  
  change_to_elem(ev: SyntheticEvent<*>): void {
    const item = this.props.app.get_item_png(ev.currentTarget.dataset.path);
    
    if (item != null) {
      const item_png = item;
      
      item_png.open(() => {
        item_png.scroll();
      });
    }
  }
  
  
  
  change_row(ev: SyntheticKeyboardEvent<*>): void {
    if (this.state.current_row === -1 || ! _.includes(Stats.keys_rows, ev.key))
      return;
    
    let current_row = _.cloneDeep(this.state.current_row);
    
    if (ev.key === 'ArrowUp' && current_row > 0)
      current_row--;
    else if (ev.key === 'ArrowDown' && current_row < this.props.stats.rows.length - 1)
      current_row++;
    else if (ev.key === 'PageUp')
      current_row = Math.max(0, current_row - 5);
    else if (ev.key === 'PageDown')
      current_row = Math.min(this.props.stats.rows.length - 1, current_row + 5);
    else if (ev.key === 'Home')
      current_row = 0;
    else if (ev.key === 'End')
      current_row = this.props.stats.rows.length - 1;
    
    this.click_row(current_row);
  }
  
  
  
  change_image(ev: SyntheticKeyboardEvent<*>): void {
    const dir   = takeDir(this.props.stats.culti),
          {app} = this.props,
          {png} = this.props.stats,
          item  = ev.key === 'ArrowLeft' ? app.prev_img(dir, png) : app.next_img(dir, png);
    
    if (item != null) {
      const item_png = item;
      
      item_png.toggle(() => {
        item_png.scroll();
      });
    }
  }
  
  
  
  key_down(ev: SyntheticKeyboardEvent<*>): void {
    if (ev.key === 'ArrowLeft' || ev.key === 'ArrowRight') {
      this.change_image(ev);
    }
    else {
      this.change_row(ev);
    }
  }
  
  
  
  render() {
    const total_has = (
      _.has(this.props.app.map_ui.map.Shp_List, this.props.stats.culti) ?
        this.props.app.map_ui.map.Shp_List[this.props.stats.culti].obj.total_has
      :
        0
    );
    
    const snum = gen_snum(this.props.num_decimals);
    
    return (
      <div>
        {(this.state.good_elems.length > 1) &&
          <div className='div_elems'>
            {this.state.good_elems.map((pe, num) => {
              const path = {'data-path': pe.png_path},
                    fontWeight = takeFilename(pe.png_path) === this.props.stats.png ? 'bold' : 'normal';
              
              return (
                <a key={num} {...path} onClick={this.change_to_elem} style={{fontWeight}}>
                  {unFuck(pe.elem)}
                </a>
              );
            })}
          </div>
        }
        
        <table className='stats_table' tabIndex='0' onKeyDown={this.key_down}>
          <thead>
            <tr>
              <th>{this.toggle_sort('ID'      )}</th>
              <th>{this.toggle_sort('Nombre'  )}</th>
              <th>{this.toggle_sort('Has'     )}</th>
              <th>{this.toggle_sort('Min'     )}</th>
              <th>{this.toggle_sort('Max'     )}</th>
              <th>{this.toggle_sort('Promedio')}</th>
              <th>{this.toggle_sort('CV'      )}</th>
            </tr>
          </thead>
          
          <tfoot>
            <tr>
              <td />
              <td>Total</td>
              <td>{snum(total_has || 0                 )}</td>
              <td>{snum(this.props.stats.total.Min     )}</td>
              <td>{snum(this.props.stats.total.Max     )}</td>
              <td>{snum(this.props.stats.total.Promedio)}</td>
              <td>{snum(this.props.stats.total.CV      )}</td>
            </tr>
          </tfoot>
          
          <tbody>
            {this.custom_sort(this.props.stats.rows).map((row: T_StatRow) => {
              const color = (this.props.sel_pol &&
                           this.props.stats.culti === this.props.sel_pol.path &&
                           row.ID === this.props.sel_pol.id + 1 ?
                             '#d9edf7' : 'white'),
                    ref = {},
                    num = {'data-num': row.ID - 1};
              
              if (color !== 'white') {
                ref.ref = tr_hilight => this.tr_hilight = tr_hilight;
              }
              
              return (
                <tr key={row.ID} {...ref} style={{backgroundColor: color}} {...num} onClick={this.click_row}>
                  <td>{row.ID}</td>
                  <td>{row.Nombre ? abrev(row.Nombre,10) : 'Sin nombre'}</td>
                  <td>{snum(row.Has     )}</td>
                  <td>{snum(row.Min     )}</td>
                  <td>{snum(row.Max     )}</td>
                  <td>{snum(row.Promedio)}</td>
                  <td>{snum(row.CV      )}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/Stats.jsx